import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';

import Config from '../config/Config.js';
import ProjectCard from '../components/project/ProjectCard';
import { RESOLVE_LOCAL_USER_STATE } from '../components/auth/AuthActions';
import UserCard from '../components/user/UserCard';
import SweetLoadingSpinner from "../components/eyeCandy/SweetLoadingSpinner.js";
import { FETCH_USER, FETCH_EXTENDED_USER } from '../components/profile/ProfileActions.js';

const PAGE_SIZE = 20;

const tabStyle = (active) => ({
  fontSize: 14, fontWeight: 600, textDecoration: 'none',
  padding: '5px 12px', borderRadius: 6, cursor: 'pointer',
  border: 'none', background: active ? '#59c6c1' : 'transparent',
  color: active ? '#0f172a' : 'var(--muted)',
  transition: 'all 100ms ease',
});

export default function ProfilePage() {
  const { username } = useParams();

  const [auth_user] = useState(RESOLVE_LOCAL_USER_STATE());
  const [profile_user, setProfileUser] = useState('');
  const [profile_extended_user, setProfileExtendedUser] = useState('');
  const [projects, setProjects] = useState([]);
  const [projectCount, setProjectCount] = useState(0);
  const [nextUrl, setNextUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [tab, setTab] = useState('projects');
  const [filter, setFilter] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const u = await FETCH_USER(username);
        const [ext, projectsRes] = await Promise.all([
          FETCH_EXTENDED_USER(u.id),
          axios.get(`${Config().baseUrl}?owner=${u.id}&publish=true&limit=${PAGE_SIZE}`),
        ]);
        if (!cancelled) {
          setProfileUser(u);
          setProfileExtendedUser(ext);
          setProjects(projectsRes.data.results);
          setProjectCount(projectsRes.data.count);
          setNextUrl(projectsRes.data.next?.replace('http://', 'https://') || null);
          setLoading(false);
        }
      } catch (e) {
        console.error('ProfilePage Load Error:', e);
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [username]);

  const loadMore = useCallback(() => {
    if (!nextUrl || loadingMore) return;
    setLoadingMore(true);
    axios.get(nextUrl)
      .then(res => {
        setProjects(prev => [...prev, ...res.data.results]);
        setNextUrl(res.data.next?.replace('http://', 'https://') || null);
      })
      .catch(err => console.error('ProfilePage loadMore error:', err))
      .finally(() => setLoadingMore(false));
  }, [nextUrl, loadingMore]);

  const auth_user_pk = auth_user?.pk;
  const profile_user_pk = profile_user?.pk;
  const isOwnProfile = !!auth_user_pk && !!profile_user_pk && String(auth_user_pk) === String(profile_user_pk);
  const hasCv = !!(profile_extended_user?.cv);

  const q = filter.toLowerCase();
  const filtered = q
    ? projects.filter(p =>
        (p.title || '').toLowerCase().includes(q) ||
        (p.subtitle || '').toLowerCase().includes(q) ||
        (p.technology || '').toLowerCase().includes(q) ||
        (p.skill || '').toLowerCase().includes(q)
      )
    : projects;

  return (
    <div className="container" style={{ paddingTop: isOwnProfile ? '20px' : '50px' }}>
      {/* Own-profile banner */}
      {isOwnProfile && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 14px', marginBottom: 20,
          background: 'var(--bg-soft)', border: '1px solid var(--border)',
          borderRadius: 8, fontSize: 13,
        }}>
          <span style={{ color: 'var(--muted)' }}>
            This is your public profile — how others see you.
          </span>
          <a href="/home" style={{ color: '#59c6c1', textDecoration: 'none', fontWeight: 600, fontSize: 13 }}>
            Back to home
          </a>
        </div>
      )}

      <div className="row">
        <div className="col-md-3 col-sm-3 col-xs-12">
          <SweetLoadingSpinner loadingState={loading} />
          <UserCard
            user={profile_user}
            extended_user={profile_extended_user}
            back_home={isOwnProfile}
          />
        </div>

        <div className="col-md-9 col-sm-9">
          {/* Tabs */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
            <button type="button" style={tabStyle(tab === 'projects')} onClick={() => setTab('projects')}>
              Projects
              {!loading && <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink)', marginLeft: 6, opacity: 0.5 }}>{projectCount}</span>}
            </button>
            {hasCv && (
              <button type="button" style={tabStyle(tab === 'cv')} onClick={() => setTab('cv')}>
                CV
              </button>
            )}
          </div>
          <hr />

          {tab === 'projects' && (
            <div className="container">
              <input
                type="text"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Filter projects..."
                style={{
                  width: '100%', padding: '8px 12px', marginBottom: 12,
                  border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
                  background: 'var(--surface)', color: 'var(--ink)', fontSize: 14,
                }}
              />
              {loading ? (
                <SweetLoadingSpinner loadingState={true} />
              ) : filtered.length === 0 ? (
                <div style={{ color: 'var(--muted)', fontSize: 14, padding: '20px 0' }}>
                  {filter ? `No projects matching "${filter}".` : 'No published projects yet.'}
                </div>
              ) : (
                <div className="row">
                  {filtered.map(project => (
                    <ProjectCard project={project} key={project.id} />
                  ))}
                </div>
              )}
              {nextUrl && (
                <div className="text-center py-3">
                  <button
                    className="btn btn-outline-secondary"
                    onClick={loadMore}
                    disabled={loadingMore}
                  >
                    {loadingMore ? 'Loading...' : 'Load more'}
                  </button>
                </div>
              )}
            </div>
          )}

          {tab === 'cv' && hasCv && (
            <div className="container">
              <div className="custom-body-markdown" style={{ fontSize: 14, lineHeight: 1.7 }}>
                <ReactMarkdown>{profile_extended_user.cv}</ReactMarkdown>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
